import { useCallback, useRef, useEffect } from "react";
import type { PendingApproval } from "../../shared/types";

type StoredPendingApproval = PendingApproval & { error?: string };

const FLUSH_DELAY_MS = 50;

function mergeApprovals(
  existing: StoredPendingApproval[],
  incoming: PendingApproval[],
): StoredPendingApproval[] {
  const merged = [...existing];
  for (const approval of incoming) {
    const isNew = !merged.some(
      (a) => a.toolApprovalId === approval.toolApprovalId,
    );
    if (isNew) {
      merged.push(approval);
    }
  }
  return merged;
}

export function useApprovalBuffer(
  setPendingApprovals: React.Dispatch<
    React.SetStateAction<StoredPendingApproval[]>
  >,
): {
  handleToolApprovalRequested: (approval: PendingApproval) => void;
  reset: () => void;
} {
  const bufferRef = useRef<PendingApproval[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    clearTimeout(flushTimerRef.current ?? undefined);
    flushTimerRef.current = null;
    bufferRef.current = [];
  }, []);

  useEffect(() => reset, [reset]);

  const handleToolApprovalRequested = useCallback(
    (approval: PendingApproval) => {
      const isNew = !bufferRef.current.some(
        (a) => a.toolApprovalId === approval.toolApprovalId,
      );
      if (isNew) {
        bufferRef.current.push(approval);
      }

      clearTimeout(flushTimerRef.current ?? undefined);
      flushTimerRef.current = setTimeout(() => {
        const buffered = bufferRef.current;
        bufferRef.current = [];
        flushTimerRef.current = null;

        if (buffered.length === 0) return;

        setPendingApprovals((prev) => mergeApprovals(prev, buffered));
      }, FLUSH_DELAY_MS);
    },
    [setPendingApprovals],
  );

  return { handleToolApprovalRequested, reset };
}
